import React, { useState, useCallback, useEffect } from "react";
import Cropper from "react-easy-crop";
import { Slider } from "@mui/material";
import { getCroppedImg } from "./cropImage";
import "./photoEditor.css";
import UploadAgain from "./assets/uploadAgain.png";
import InfoIcon from "./assets/infoIcon.png";

/**
 * Props:
 * - file: The File object from the uploader
 * - onUploadNew: callback to reset / pick a new file
 * - onImageEdited: callback to pass the final cropped image to the parent
 */
const PhotoEditor = ({ file, onUploadNew, onImageEdited }) => {
  const [imageSrc, setImageSrc] = useState("");
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [croppedImage, setCroppedImage] = useState(null);

  // Convert the file to a data URL
  useEffect(() => {
    if (file) {
      const reader = new FileReader();
      reader.addEventListener("load", () => {
        setImageSrc(reader.result);
      });
      reader.readAsDataURL(file);
    }
  }, [file]);

  // Called each time the crop/zoom/rotate changes
  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  // Generate the final cropped image
  const showCroppedImage = useCallback(async () => {
    try {
      const croppedDataUrl = await getCroppedImg(
        imageSrc,
        croppedAreaPixels,
        rotation
      );
      setCroppedImage(croppedDataUrl);
      // Pass the cropped image data URL to the parent component
      if (onImageEdited) {
        onImageEdited(croppedDataUrl); // This will be handled in ProfilePhoto
      }
    } catch (e) {
      console.error(e);
    }
  }, [imageSrc, croppedAreaPixels, rotation, onImageEdited]);

  const handleUploadNewPhoto = () => {
    onUploadNew();
  };

  return (
    <div className="advanced-editor-wrapper">
      {/* Left side: the cropper */}
      <div className="cropper-container">
        {imageSrc && (
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            rotation={rotation}
            aspect={1} // 1 = square aspect ratio
            cropShape="round" // Circular overlay
            showGrid={false} // Hide grid lines if you prefer
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onRotationChange={setRotation}
            onCropComplete={onCropComplete}
            restrictPosition={false}
          />
        )}
      </div>

      {/* Right side: controls and preview */}
      <div className="controls-container">
        <h3 className="controls-title">Make adjustments</h3>

        {/* ZOOM Slider */}
        <div className="slider-container">
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "5px",
              marginBottom: "5px",
            }}
          >
            <p className="controls-label">Zoom</p>
            <img src={InfoIcon} style={{ height: "20px", width: "20px" }} />
          </div>

          <Slider
            value={zoom}
            onChange={(e, newValue) => setZoom(newValue)}
            min={1}
            max={3}
            step={0.01}
            valueLabelDisplay="off"
            sx={{
              color: "#333",
              height: 8,
              width: 300,
              "& .MuiSlider-track": {
                border: "none",
              },
              "& .MuiSlider-thumb": {
                height: 16,
                width: 16,
                backgroundColor: "#333333",
              },
              "& .MuiSlider-rail": {
                opacity: 1,
                backgroundColor: "#EFEFEF",
              },
            }}
          />
        </div>

        {/* ROTATE Slider */}
        <div className="slider-container">
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "5px",
              marginBottom: "5px",
            }}
          >
            <p className="controls-label">Rotate</p>
            <img src={InfoIcon} style={{ height: "20px", width: "20px" }} />
          </div>

          <Slider
            value={rotation}
            onChange={(e, newValue) => setRotation(newValue)}
            min={-180}
            max={180}
            step={1}
            valueLabelDisplay="off"
            sx={{
              color: "#333",
              height: 8,
              width: 300,
              "& .MuiSlider-track": {
                border: "none",
              },
              "& .MuiSlider-thumb": {
                height: 16,
                width: 16,
                backgroundColor: "#333333",
              },
              "& .MuiSlider-rail": {
                opacity: 1,
                backgroundColor: "#EFEFEF",
              },
            }}
          />
        </div>

        <div className="buttons-row">
          <button className="new-photo-upload" onClick={handleUploadNewPhoto}>
            <img src={UploadAgain} style={{ width: "16px", height: "16px" }} />
            Upload new photo
          </button>
          <button onClick={showCroppedImage}>Save Crop</button>
        </div>
      </div>
    </div>
  );
};

export default PhotoEditor;
